import { createFileRoute, notFound } from "@tanstack/react-router"
import { BookOpen, Route as RouteIcon } from "lucide-react"

import { breadcrumbJsonLd, buildHead, webPageJsonLd } from "@/core/seo"

import {
  Cites,
  CompareHeader,
  CompareTable,
  HowWeCheck,
  Legend,
  Section,
  SourceList,
  linkClass,
} from "../components/compare/compare-parts"
import { formatCheckedOn } from "../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, FaqAccordion } from "../components/landing"
import { PickPair } from "../components/landing/page-parts"
import { driversnoteAlternative as page } from "../content/compare"
import { COMPARE_PUBLISHED, compareReviewed } from "../content/compare/publish"

const PATH = "/compare/driversnote-alternative"

/**
 * Copy source: docs/design/landing/copy-compare.md section 2, as cut by T407 D2 (no data-location
 * row), D4 (no 12-week row), D5 (no prices of ours). Every Yes in our column must match a released
 * feature (D1).
 */
export const driversnoteSeo = () =>
  buildHead({
    title: "Driversnote alternative for sole traders",
    description:
      "Kestly vs Driversnote: free plan, price, receipts, invoices and exports, each with its source and date. Includes where Driversnote is stronger.",
    path: PATH,
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Compare", path: "/compare" },
        { name: "Driversnote alternative" },
      ]),
      webPageJsonLd({
        name: "Kestly vs Driversnote",
        path: PATH,
        dateModified: compareReviewed[PATH],
      }),
    ],
  })

export const Route = createFileRoute("/_brand-layout/compare/driversnote-alternative")({
  beforeLoad: () => {
    if (!COMPARE_PUBLISHED) throw notFound()
  },
  head: () => {
    const { meta, links, scripts } = driversnoteSeo()
    return { meta, links, scripts }
  },
  component: DriversnotePage,
})

const FAQ = [
  {
    question: "Is Kestly cheaper than Driversnote?",
    answer: (
      <>
        Not like for like. Driversnote Pro is a mileage plan at A$17 a month excl. GST{" "}
        <Cites ids={["S1"]} />. Kestly&apos;s plans are on our{" "}
        <a href="/pricing" className={linkClass}>
          pricing page
        </a>
        ; compare what each plan includes, not only the number.
      </>
    ),
  },
  {
    question: "Is either app approved by the ATO?",
    answer: (
      <>
        We do not use that phrase and neither should you. Driversnote describes its app and reports
        as ATO-compliant <Cites ids={["S2", "S4"]} />. The rules are on the ATO logbook page; check
        any app&apos;s report against them, or ask a registered tax agent.
      </>
    ),
  },
  {
    question: "Can I move my trips from Driversnote?",
    answer: "Import is not in the first release. Export your old reports and start new trips here.",
  },
  {
    question: "How current is this page?",
    answer: `Driversnote facts were opened on ${formatCheckedOn(compareReviewed[PATH])} and are re-checked every 90 days. If something here is out of date, tell us and we will fix it.`,
  },
]

export function DriversnotePage() {
  const reviewed = compareReviewed[PATH]
  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Compare", href: "/compare" },
          { label: "Driversnote alternative" },
        ]}
      />
      <CompareHeader reviewed={reviewed} title="Kestly vs Driversnote">
        <AnswerBox>
          Driversnote&apos;s site describes automatic mileage tracking and logbook reports, with a
          free plan of up to 15 trips a month <Cites ids={["S1", "S2"]} />. Its pricing, feature and
          reporting pages do not describe receipt capture or invoicing{" "}
          <Cites ids={["S1", "S2", "S3", "S4"]} />. Kestly includes both. If you only need trips, or
          you manage a team of drivers, Driversnote can be the better fit.
        </AnswerBox>
      </CompareHeader>
      <Section title="Side by side">
        <Legend checked={formatCheckedOn(reviewed)} notStated />
        <CompareTable
          caption="Side by side"
          page={page}
          oursHeader="Kestly"
          theirsHeader="Driversnote"
        />
        <p className="text-muted-foreground text-sm">
          Driversnote cells come from Driversnote&apos;s own pages, opened{" "}
          {formatCheckedOn(reviewed)}. “Not stated” means we did not find it on those pages; it does
          not mean the feature is missing. A$17 is Driversnote&apos;s price excluding GST.
        </p>
      </Section>
      <Section title="Where Driversnote is stronger">
        <ul className="grid list-disc gap-1.5 pl-5">
          <li>
            Optional iBeacon to reduce missed trips <Cites ids={["S1", "S7"]} />.
          </li>
          <li>
            Team plans with admin report collection <Cites ids={["S1"]} />.
          </li>
          <li>
            Reports can be sent from the app as PDF or Excel <Cites ids={["S7"]} />.
          </li>
          <li>
            Several vehicles and workplaces <Cites ids={["S3"]} />, and a web app with bulk editing{" "}
            <Cites ids={["S7"]} />.
          </li>
        </ul>
      </Section>
      <Section title="Where Kestly is different">
        <ul className="grid list-disc gap-1.5 pl-5">
          <li>Trips, receipts and tax invoices in one place for a sole trader with an ABN.</li>
          <li>
            Trips and records within the free limits stay visible and exportable; trips over the GPS
            limit are kept but hidden until you upgrade.
          </li>
        </ul>
      </Section>
      <Section title="Who should pick which">
        <PickPair
          items={[
            {
              icon: RouteIcon,
              tone: "warning",
              title: "Pick Driversnote if",
              body: "You are an employee or run a team, you only need trips, and you want a mature app with a beacon option.",
            },
            {
              icon: BookOpen,
              tone: "primary",
              title: "Pick Kestly if",
              body: "You have an ABN and want trips, receipts and invoices together, ready for your accountant.",
            },
          ]}
        />
      </Section>
      <HowWeCheck />
      <Section title="Questions">
        <FaqAccordion items={FAQ} />
      </Section>
      <Section title="Sources">
        <SourceList page={page} />
        <p className="text-muted-foreground text-sm">
          Kestly is not affiliated with Driversnote. Driversnote is a trade mark of its owner.
        </p>
      </Section>
      <CtaBand
        title="See it with your own trips"
        description="Create a free account and record your first trips."
        ctaLabel="Create a free account"
        ctaHref="/login"
      />
    </>
  )
}
